import { useCallback, useEffect, useState } from "react";

import { Button } from "../components/ui/button";
import { Badge, Input, Select, Separator, Table, Td, Th } from "../components/ui/misc";
import { api } from "../lib/api";

type Department = { id: string; name: string; code: string; status: string; member_count?: number };
type Member = {
  id: string;
  department_id: string;
  department_name: string;
  email: string;
  display_name: string | null;
  role: string;
  status: string;
};

const ROLE_LABEL: Record<string, string> = { admin: "管理员", leader: "主管", employee: "成员" };

/** 组织管理：部门 + 成员（只有管理员能改；其他人打开会看到 403 提示）。 */
export function Admin() {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [me, setMe] = useState<{ email: string; role: string } | null>(null);
  const [newDepartment, setNewDepartment] = useState("");
  const [newMember, setNewMember] = useState({
    email: "",
    displayName: "",
    role: "employee",
    departmentId: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [meResponse, departmentResponse, memberResponse] = await Promise.all([
        api.me(),
        api.departments(),
        api.members(),
      ]);
      setMe(meResponse.actor);
      setDepartments(departmentResponse.departments);
      setMembers(memberResponse.members);
      setNewMember((current) => ({
        ...current,
        departmentId: current.departmentId || departmentResponse.departments[0]?.id || "",
      }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "加载失败");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      await load();
      setStatus(message);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "操作失败");
      setStatus(null);
    } finally {
      setBusy(false);
    }
  };

  const isAdmin = me?.role === "admin";

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <div className="flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-white px-4 py-3">
        <div>
          <div className="text-sm font-semibold">组织管理</div>
          <div className="text-xs text-[var(--color-muted-foreground)]">
            当前登录：{me?.email ?? "—"}（{ROLE_LABEL[me?.role ?? ""] ?? "—"}）
            {isAdmin ? " · 可跨部门查看全部方案" : " · 只能看到本部门（增删部门和成员需要总管理员）"}
          </div>
        </div>
        {status ? <span className="text-xs text-emerald-700">{status}</span> : null}
      </div>

      {error ? (
        <p className="rounded-lg border border-[var(--color-border)] bg-white px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}

      <div className="grid grid-cols-[320px_1fr] gap-4">
        {/* 部门 */}
        <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-border)] bg-white p-4">
          <div className="text-sm font-semibold">部门（{departments.length}）</div>
          <div className="flex gap-2">
            <Input
              placeholder="新部门名称，例如 售后部"
              value={newDepartment}
              onChange={(event) => setNewDepartment(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && newDepartment.trim()) {
                  void run(() => api.createDepartment({ name: newDepartment.trim() }), "部门已创建");
                  setNewDepartment("");
                }
              }}
            />
            <Button
              disabled={!isAdmin || busy || !newDepartment.trim()}
              onClick={() => {
                void run(() => api.createDepartment({ name: newDepartment.trim() }), "部门已创建");
                setNewDepartment("");
              }}
            >
              新增
            </Button>
          </div>
          <Separator />
          <div className="flex flex-col gap-1">
            {departments.map((department) => (
              <div
                key={department.id}
                className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-[var(--color-muted)]"
              >
                <input
                  aria-label={`部门名称 ${department.name}`}
                  className="w-full bg-transparent text-sm"
                  defaultValue={department.name}
                  disabled={!isAdmin}
                  onBlur={(event) => {
                    const name = event.target.value.trim();
                    if (name && name !== department.name) {
                      void run(() => api.updateDepartment(department.id, { name }), "部门已改名");
                    }
                  }}
                />
                <Badge tone="muted">{department.member_count ?? 0} 人</Badge>
                <button
                  type="button"
                  disabled={!isAdmin || (department.member_count ?? 0) > 0 || busy}
                  title={(department.member_count ?? 0) > 0 ? "部门下还有成员，先把成员移走" : "删除部门"}
                  className="ml-2 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)] disabled:opacity-30"
                  onClick={() => void run(() => api.deleteDepartment(department.id), "部门已删除")}
                >
                  删除
                </button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            部门名直接改、失焦即保存。跨部门不共享方案：成员只能看本部门（管理员除外）。
          </p>
        </div>

        {/* 成员 */}
        <div className="flex flex-col gap-3 rounded-xl border border-[var(--color-border)] bg-white p-4">
          <div className="text-sm font-semibold">成员（{members.length}）</div>
          <div className="grid grid-cols-12 gap-2">
            <Input
              className="col-span-4"
              placeholder="邮箱（登录用，例如 name@yijianspace.com）"
              value={newMember.email}
              onChange={(event) => setNewMember({ ...newMember, email: event.target.value })}
            />
            <Input
              className="col-span-3"
              placeholder="姓名（可选）"
              value={newMember.displayName}
              onChange={(event) => setNewMember({ ...newMember, displayName: event.target.value })}
            />
            <Select
              className="col-span-2"
              aria-label="角色"
              value={newMember.role}
              onChange={(event) => setNewMember({ ...newMember, role: event.target.value })}
            >
              <option value="employee">成员</option>
              <option value="leader">主管</option>
              <option value="admin">管理员</option>
            </Select>
            <Select
              className="col-span-2"
              aria-label="部门"
              value={newMember.departmentId}
              onChange={(event) => setNewMember({ ...newMember, departmentId: event.target.value })}
            >
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
            <Button
              className="col-span-1"
              disabled={!isAdmin || busy || !newMember.email.includes("@")}
              onClick={() => {
                void run(
                  () =>
                    api.createMember({
                      department_id: newMember.departmentId,
                      email: newMember.email.trim(),
                      display_name: newMember.displayName.trim() || null,
                      role: newMember.role,
                    }),
                  "成员已添加",
                );
                setNewMember({ ...newMember, email: "", displayName: "" });
              }}
            >
              添加
            </Button>
          </div>

          <Table>
            <thead>
              <tr>
                <Th>邮箱</Th>
                <Th>姓名</Th>
                <Th className="w-40">部门</Th>
                <Th className="w-32">角色</Th>
                <Th className="w-24">状态</Th>
                <Th className="w-20" />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <Td className="font-mono text-xs">{member.email}</Td>
                  <Td className="text-xs">{member.display_name ?? "—"}</Td>
                  <Td>
                    <Select
                      aria-label={`部门 ${member.email}`}
                      value={member.department_id}
                      disabled={!isAdmin}
                      onChange={(event) =>
                        void run(
                          () => api.updateMember(member.id, { department_id: event.target.value }),
                          "已调整部门",
                        )
                      }
                    >
                      {departments.map((department) => (
                        <option key={department.id} value={department.id}>
                          {department.name}
                        </option>
                      ))}
                    </Select>
                  </Td>
                  <Td>
                    <Select
                      aria-label={`角色 ${member.email}`}
                      value={member.role}
                      disabled={!isAdmin}
                      onChange={(event) =>
                        void run(
                          () => api.updateMember(member.id, { role: event.target.value }),
                          "已调整角色",
                        )
                      }
                    >
                      <option value="employee">成员</option>
                      <option value="leader">主管</option>
                      <option value="admin">管理员</option>
                    </Select>
                  </Td>
                  <Td>
                    <button
                      type="button"
                      disabled={!isAdmin}
                      className="text-xs hover:underline"
                      onClick={() =>
                        void run(
                          () =>
                            api.updateMember(member.id, {
                              status: member.status === "active" ? "suspended" : "active",
                            }),
                          member.status === "active" ? "已停用" : "已启用",
                        )
                      }
                    >
                      <Badge tone={member.status === "active" ? "ok" : "warn"}>
                        {member.status === "active" ? "启用中" : "已停用"}
                      </Badge>
                    </button>
                  </Td>
                  <Td className="text-right">
                    <button
                      type="button"
                      disabled={!isAdmin || busy}
                      className="mr-2 text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] disabled:opacity-30"
                      onClick={() => {
                        const password = window.prompt(
                          `给 ${member.email} 设置新密码（至少 8 位，含字母和数字）`,
                        );
                        if (!password) return;
                        void run(
                          () => api.setMemberPassword(member.id, password),
                          `已重置 ${member.email} 的密码`,
                        );
                      }}
                    >
                      设置密码
                    </button>
                    <button
                      type="button"
                      disabled={!isAdmin || member.email === me?.email}
                      className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-destructive)] disabled:opacity-30"
                      onClick={() => void run(() => api.deleteMember(member.id), "已移除成员")}
                    >
                      移除
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            成员用邮箱登录（接 Cloudflare Access
            后按邮箱放行）。一人只属于一个部门；停用后无法登录，但历史方案保留。
          </p>
        </div>
      </div>
    </div>
  );
}
